import { useEffect, useRef } from 'react';

import { useSearchParams } from 'react-router-dom';

import { useAppDispatch, useAppSelector } from 'app/hooks';
import OhsDocumentFilterModels from 'documents/register/search/OhsDocumentFilterModels';
import { fetchDocumentsListsAsync, getOhsDocumentsModuleState } from 'documents/OhsDocumentsSlice';
import { OhsTabKeyTypes } from 'global-components/tab/OhsTabModels';
import { ParamTypes } from 'global-services/register/OhsRegisterModels';

export default function useOhsDocumentList(allowFetch?: boolean) {
  const dispatch = useAppDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const paramString = searchParams.toString();
  const prevParamsRef = useRef<string | null>(null);

  const { folderView, displayList, page, tab, archived, buildFilters, buildResetFilter, category } =
    new OhsDocumentFilterModels({ searchParams });

  const folderViewBooleanString = folderView ? '1' : '0';

  const documentListReducer = useAppSelector(getOhsDocumentsModuleState);
  const { isLoading, documentsList } = documentListReducer;

  const fetchDocumentLists = async ({
    newParams = searchParams,
    newTab = tab,
  }: {
    newParams?: URLSearchParams;
    newTab?: OhsTabKeyTypes.Documents | OhsTabKeyTypes.Workplace | OhsTabKeyTypes.Template;
  } = {}) => {
    const newFilters = new OhsDocumentFilterModels({ searchParams: newParams });

    const payload = {
      list: newFilters.hasSearch ? newFilters.searchFilter : newFilters.filter,
      count: newFilters.hasSearch ? newFilters.searchFilterCount : newFilters.filterCount,
      hasSearch: newFilters.hasSearch,
      tab: newTab,
    };

    dispatch(fetchDocumentsListsAsync(payload));
  };

  const updateFilter = (payload: number | any) => {
    const newParams = buildFilters(payload, documentsList?.pagination?.next);
    setSearchParams(newParams);
  };

  const resetFilters = () => {
    const newParams = buildResetFilter();
    setSearchParams(newParams);
  };

  const changeTab = (tabKey: OhsTabKeyTypes) => {
    setSearchParams({ tab: tabKey });
  };

  const changeFolderView = (val?: boolean) => {
    const newFolderView = val ?? !folderView;
    setSearchParams({ tab, folderView: newFolderView ? '1' : '0' });
  };

  const changeCategory = (newCategory: string) => {
    setSearchParams({
      tab,
      folderView: folderViewBooleanString,
      category: newCategory,
    });
  };

  const changeSubcategory = (newSubcategory: string) => {
    const payload = {
      tab,
      folderView,
      [ParamTypes.Category]: category,
      [ParamTypes.Subcategory]: newSubcategory,
      [ParamTypes.Categories]: [category],
      [ParamTypes.SubCategories]: [newSubcategory],
    };
    const newParams = buildFilters(payload);
    setSearchParams(newParams);
  };

  const backToCategories = () => {
    setSearchParams({
      tab,
      folderView: folderViewBooleanString,
    });
  };

  useEffect(() => {
    if (displayList && allowFetch && prevParamsRef.current !== paramString) {
      prevParamsRef.current = paramString;
      fetchDocumentLists();
    }
  }, [paramString]);

  return {
    isLoading,
    documentsList,
    currentPage: page,
    setCurrentPage: (value: number) => updateFilter(value),
    updateFilter,
    fetchDocumentLists,
    resetFilters,
    archived,
    changeTab,
    changeFolderView,
    changeCategory,
    changeSubcategory,
    backToCategories,
  } as const;
}
